"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import OrderService, { OrderListItem, OrderListFilters, ORDER_STATUS,ORDER_STATUS_NAME,ORDER_STATUS_LABELS, STATUS_BADGE_STYLES, NEXT_STATUS_MAP, getNextStatusTransition } from "../services/order-service";
import BranchService from "../../branch/services/branch-service";
import { showToast } from "../../../components/custom/custom-toast";
import DropdownSelectOne from "../../../components/custom/dropdown-select-one";
import { MultiSelectDropdown } from "../../../components/custom/multi-select-dropdown";
import OrderDetailModal from "../components/OrderDetailModal";
import OrderUpdateModal from "../components/OrderUpdateModal";
import OrderPayModal from "../components/OrderPayModal";
import OrderCancelModal from "../components/OrderCancelModal";
import OrderEmployeeWeightModal from "../components/OrderEmployeeWeightModal";
import OrderChangeStatusModal from "../components/OrderChangeStatusModal";
import SplitOrderModal from "../components/SplitOrderModal";
import { useDebounce } from "../../../hooks/use-debounce";
import SelectDropdownAsyncPaginate from "../../../components/custom/select-dropdown-async-paginate";
import { API_USER_ROUTER } from "../../../services/api-config";
import { useOrderRealtime, useSocketConnection } from "../../../hooks/useSocket";
import socketService, { OrderEventData } from "../../../services/socket.service";
import { useBranchContext } from "../../../contexts/BranchContext";
import EquipmentService, { EQUIPMENT_TYPE_NAME, OrderEquipmentSummaryItem } from "../../equipment/services/equipment-service";
import EmptyState from "../../../components/custom/empty-state";
import DateInputCustom from "../../../components/custom/date-input/date-input-custom";
import OrderExportButton from "../components/OrderExportButton";
import { usePermission } from "../../../hooks/usePermission";
import { PERMISSION_CODE } from "../../../constants/permission";
import { formatKg } from "../../../utils/helpers";

// =========================
// Helpers
// =========================
const formatCurrency = (value: unknown): string => {
  const numeric = Number(value) || 0;
  return numeric.toLocaleString("vi-VN");
};

const IS_DEBIT_BADGE_STYLES: Record<number, string> = {
  0: "bg-green-100 text-green-700",
  1: "bg-red-100 text-red-700",
};

const IS_DEBIT: Record<number, string> = {
  0: "Thanh toán liền",
  1: "Nợ",
};
const PAYMENT_METHOD_BADGE_STYLES: Record<number, string> = {
  1: "bg-green-100 text-green-700",
  2: "bg-red-100 text-red-700",
};

const PAYMENT_METHOD: Record<number, string> = {
  1: "Tiền mặt",
  2: "Chuyển khoản",
};

// Các trạng thái cho ô lọc (chọn được nhiều) — xếp theo thứ tự luồng đơn.
const STATUS_FILTER_OPTIONS: { value: number; label: string }[] = [
  // Nhận & Lấy hàng
  { value: ORDER_STATUS.WAITING_ACCEPT, label: ORDER_STATUS_NAME['WAITING_ACCEPT'] },
  { value: ORDER_STATUS.WAITING_PICK_UP, label: ORDER_STATUS_NAME['WAITING_PICK_UP'] },
  // Kiểm đếm với khách — CHỈ đơn kiểm đếm (WAITING_ACCEPT → COUNTING → COUNTING_DONE → WAITING_DRIVER → lấy hàng)
  { value: ORDER_STATUS.COUNTING, label: ORDER_STATUS_NAME['COUNTING'] + ' (đơn kiểm đếm)' },
  { value: ORDER_STATUS.COUNTING_DONE, label: ORDER_STATUS_NAME['COUNTING_DONE'] + ' (đơn kiểm đếm)' },
  { value: ORDER_STATUS.WAITING_DRIVER, label: ORDER_STATUS_NAME['WAITING_DRIVER'] + ' (đơn kiểm đếm)' },
  // Xử lý — quy trình hiện tại: Kiểm tra → Đang sản xuất → (đóng túi) → Sẵn sàng giao
  { value: ORDER_STATUS.INSPECTING, label: ORDER_STATUS_NAME['INSPECTING'] },
  { value: ORDER_STATUS.WASHING, label: ORDER_STATUS_NAME['WASHING'] },
  // Điều chuyển
  { value: ORDER_STATUS.TRANSFERRING, label: ORDER_STATUS_NAME['TRANSFERRING'] },
  // Giao hàng
  { value: ORDER_STATUS.READY_DELIVERY, label: ORDER_STATUS_NAME['READY_DELIVERY'] },
  { value: ORDER_STATUS.IN_DELIVERY, label: ORDER_STATUS_NAME['IN_DELIVERY'] },
  { value: ORDER_STATUS.DELIVERY_COUNTING, label: ORDER_STATUS_NAME['DELIVERY_COUNTING'] + ' (đơn kiểm đếm)' },
  // Kết thúc
  { value: ORDER_STATUS.COMPLETE, label: ORDER_STATUS_NAME['COMPLETE'] },
  { value: ORDER_STATUS.CANCELED, label: ORDER_STATUS_NAME['CANCELED'] },
];

// =========================
// Main Component
// =========================
export default function OrderIndexPage() {
  // Lấy ngày đầu tháng hiện tại
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  // Định dạng về YYYY-MM-DD cho input type="date"
  const formatDate = (date: Date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // State giữ YYYY-MM-DD (BE dùng); DateInputCustom hiển thị dd/MM/yyyy → convert ở ranh giới.
  const toDMY = (ymd: string) => {
    const [y, m, d] = (ymd || "").split("-");
    return d && m && y ? `${d}/${m}/${y}` : "";
  };
  const toYMD = (dmy: string) => {
    const [d, m, y] = (dmy || "").split("/");
    return d && m && y ? `${y}-${m}-${d}` : "";
  };

  const [startDate, setStartDate] = useState(formatDate(firstDayOfMonth));
  const [endDate, setEndDate] = useState(formatDate(today));
  const [orders, setOrders] = useState<OrderListItem[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [branchId, setBranchId] = useState<number | "">("");
  const [branchOptions, setBranchOptions] = useState<{ value: number; label: string }[]>([]);
  // Lọc NHIỀU trạng thái — mảng rỗng = tất cả trạng thái. BE nhận `statuses` dạng mảng/chuỗi "1,2,…".
  const [statusFilter, setStatusFilter] = useState<number[]>([]);
  const isAllStatuses = statusFilter.length === 0;
  const matchesStatusFilter = useCallback(
    (status: unknown) => statusFilter.length === 0 || statusFilter.includes(Number(status)),
    [statusFilter],
  );
  // false = đơn của chi nhánh (mặc định); true = đơn đang GIỮ HỘ / SẮP NHẬN điều chuyển (kể cả đơn con)
  const [consignedView, setConsignedView] = useState(false);
  const [assignedEmployeeId, setAssignedEmployeeId] = useState<number | "">("");
  // Loại đơn: all | normal (đơn thường, requires_counting=0) | counting (đơn kiểm đếm, =1) | rewash (giặt lại)
  const [orderKind, setOrderKind] = useState<'all' | 'normal' | 'counting' | 'rewash'>('all');
  const useDebounceSearch = useDebounce(search, 1000);
  // ===== Modals =====
  const [detailOrder, setDetailOrder] = useState<OrderListItem | null>(null);
  const [editOrder, setEditOrder] = useState<OrderListItem | null>(null);
  const [payOrder, setPayOrder] = useState<OrderListItem | null>(null);
  const [cancelOrder, setCancelOrder] = useState<OrderListItem | null>(null);
  const [weightOrder, setWeightOrder] = useState<OrderListItem | null>(null);
  const [changeStatusOrder, setChangeStatusOrder] = useState<OrderListItem | null>(null);
  const [splitOrder, setSplitOrder] = useState<OrderListItem | null>(null);
  const [expandedOrders, setExpandedOrders] = useState<Set<number>>(new Set());
  const [splitLoading, setSplitLoading] = useState(false);
  const [changeStatusLoading, setChangeStatusLoading] = useState(false);
  const [equipmentPopover, setEquipmentPopover] = useState<{ orderId: number; items: OrderEquipmentSummaryItem[]; loading: boolean } | null>(null);

  const { hasPermission } = usePermission();
  const canExport = hasPermission([PERMISSION_CODE.ORDER_EXPORT]);
  const { fetch: fetchOrders } = OrderService.list();
  const { fetch: fetchDetail } = OrderService.detail();
  const { fetch: updateOrder } = OrderService.update();
  const { fetch: cancelOrderApi } = OrderService.cancel();
  const { fetch: completeOrderApi } = OrderService.complete_order();
  const { fetch: changeStatusApi } = OrderService.changeStatus();
  const { fetch: splitOrderApi } = OrderService.splitOrder();
  const { fetch: fetchEquipmentSummary } = EquipmentService.orderEquipmentSummary();
  // Chỉ lấy chi nhánh mà tài khoản hiện tại có quyền (không lấy toàn công ty)
  const { fetch: fetchBranches } = BranchService.myBranches();
  const fetchBranchesRef = useRef(fetchBranches);
  useEffect(() => { fetchBranchesRef.current = fetchBranches; }, [fetchBranches]);

  // keep a stable ref to avoid effect loops when hook identities change
  const fetchOrdersRef = useRef(fetchOrders);
  useEffect(() => { fetchOrdersRef.current = fetchOrders; }, [fetchOrders]);

  // Load branches once
  useEffect(() => {
    let mounted = true;
    (async () => {
      const res = await fetchBranchesRef.current();
      if (!mounted || !res || res.status !== 200) return;
      const list: any[] = Array.isArray(res.data)
        ? res.data
        : ((res.data as { list?: any[] })?.list ?? []);
      setBranchOptions(list.map((b: any) => ({ value: Number(b.id), label: b.name ?? `CN #${b.id}` })));
    })();
    return () => { mounted = false; };
  }, []);

  // ===== Fetch Orders =====
  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params: OrderListFilters = {
        page,
        limit,
        key_search: useDebounceSearch || undefined,
        ...(branchId ? { branch_id: Number(branchId) } : {}),
        ...(startDate ? { from_date: startDate } : {}),
        ...(endDate ? { to_date: endDate } : {}),
        ...(statusFilter.length > 0 ? { statuses: statusFilter } : {}),
        ...(assignedEmployeeId ? { assigned_employee_id: Number(assignedEmployeeId) } : {}),
        ...(orderKind === 'rewash' ? { order_type: 2 } : {}),
        ...(orderKind === 'counting' ? { requires_counting: 1 } : {}),
        ...(orderKind === 'normal' ? { requires_counting: 0 } : {}),
        ...(consignedView ? { scope: 'consigned' } : {}),
      };

      const res = await fetchOrdersRef.current(params);
      if (res?.status === 200) {
        const data: any = res.data ?? {};
        const list: any[] = Array.isArray(data.list)
          ? data.list
          : Array.isArray(data.items)
          ? data.items
          : Array.isArray(data)
          ? (data as any[])
          : [];
          
        // Lọc các bản ghi trùng lặp dựa trên ID
        const uniqueOrders = list.filter((order, index, self) =>
          index === self.findIndex((o) => o.id === order.id)
        );
        setOrders(uniqueOrders);
        const totalRaw =
          (data && (data.total_record ?? data.totalRecord ?? data.total ?? data.totalRecords)) ??
          0;
        setTotalRecords(Number(totalRaw || list.length || 0));
      } else {
        showToast({ message: "Không thể tải danh sách đơn hàng", type: "error" });
      }
    } catch (err) {
      console.error(err);
      showToast({ message: "Lỗi khi tải dữ liệu", type: "error" });
    } finally {
      setLoading(false);
    }
  }, [page, limit, useDebounceSearch, branchId, startDate, endDate, statusFilter, assignedEmployeeId, orderKind, consignedView]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      loadOrders();
    }, 500); // Delay 500ms trước khi gọi loadOrders

    return () => clearTimeout(timeout); // clear timeout khi dependency thay đổi
  }, [loadOrders]);
  // Reset về trang 1 khi đổi limit
  useEffect(() => { setPage(1); }, [limit]);

  // Socket realtime integration - cập nhật trực tiếp state không gọi API
  // Sử dụng selectedBranch từ BranchContext (đã được subscribe ở BranchProvider)
  const { selectedBranch: contextBranch } = useBranchContext();
  // Lấy trạng thái socket connection sớm (trước useEffect all-branches)
  const { isConnected, isConnecting, error: socketError } = useSocketConnection();

  // Auto-sync branchId filter với selectedBranch từ BranchContext (sidebar selector)
  useEffect(() => {
    // Khi user chọn chi nhánh từ sidebar → tự động filter danh sách đơn
    // Nếu contextBranch = null (Tất cả chi nhánh) → reset filter
    setBranchId(contextBranch !== null ? contextBranch : "");
  }, [contextBranch]);

  // Socket branch: ưu tiên context branch, sau đó là filter branchId
  const socketBranchIds = contextBranch
    ? [contextBranch]
    : (branchId ? [Number(branchId)] : branchOptions.map(b => b.value));

  // Khi xem "Tất cả chi nhánh" (contextBranch = null), BranchContext không subscribe
  // → cần tự subscribe vào tất cả branches để nhận realtime events
  useEffect(() => {
    // Chỉ subscribe khi KHÔNG có contextBranch (tức "Tất cả chi nhánh")
    // Khi có contextBranch, BranchContext đã lo subscribe
    if (contextBranch || !socketService.isConnected() || branchOptions.length === 0) return;

    const allBranchIds = branchOptions.map(b => b.value);
    console.log('[OrderList] No branch selected, subscribing to all branches:', allBranchIds);

    socketService.subscribeToOrders(allBranchIds);
    for (const bId of allBranchIds) {
      socketService.joinBranchRoom(bId);
    }

    return () => {
      for (const bId of allBranchIds) {
        socketService.leaveBranchRoom(bId);
      }
    };
  }, [contextBranch, branchOptions, isConnected]);

  // Hàm thêm order mới vào đầu list
  const addOrderToList = useCallback((newOrder: OrderListItem) => {
    setOrders(prev => {
      // Kiểm tra đã tồn tại chưa
      if (prev.some(o => o.id === newOrder.id)) return prev;
      return [newOrder, ...prev];
    });
    setTotalRecords(prev => prev + 1);
  }, []);

  // Hàm cập nhật order trong list
  const updateOrderInList = useCallback((updatedOrder: Partial<OrderListItem> & { id: number }) => {
    setOrders(prev => prev.map(o =>
      o.id === updatedOrder.id ? { ...o, ...updatedOrder } : o
    ));
  }, []);

  // Hàm xóa order khỏi list
  const removeOrderFromList = useCallback((orderId: number) => {
    setOrders(prev => prev.filter(o => o.id !== orderId));
    setTotalRecords(prev => Math.max(0, prev - 1));
  }, []);

  // Theo dõi id đơn đang mở trong OrderDetailModal (ref để không phải đưa detailOrder
  // vào dependency array của các callback realtime bên dưới)
  const detailOrderIdRef = useRef<number | null>(null);
  useEffect(() => { detailOrderIdRef.current = detailOrder?.id ?? null; }, [detailOrder?.id]);

  // Modal chi tiết đơn dùng state riêng (detailOrder), không tự nhận event realtime.
  // Nếu order vừa đổi trùng với đơn đang mở trong modal → fetch lại full detail để modal cập nhật ngay,
  // không cần đóng/mở lại (tái dùng đúng pattern refresh detail đã có ở onSaved của OrderEmployeeWeightModal).
  const syncDetailOrderIfOpen = useCallback((orderId?: number | null) => {
    if (!orderId || detailOrderIdRef.current !== orderId) return;
    fetchDetail(orderId).then(res => {
      if (res?.status === 200) setDetailOrder(res.data);
    });
  }, [fetchDetail]);

  useOrderRealtime(socketBranchIds, {
    onOrderCreated: useCallback((data: OrderEventData) => {
      console.log('[Socket] ORDER_CREATED event received:', data);
      // Backend gửi order_data, không phải order
      const orderData = data.order_data || data.order;
      if (!orderData) {
        console.log('[Socket] Order created but no order_data:', data);
        showToast({ message: `Đơn hàng mới: ${data.order_code || 'N/A'}`, type: "info" });
        return;
      }
      showToast({ message: `Đơn hàng mới: ${orderData.code || data.order_code || 'N/A'}`, type: "info" });
      // Thêm vào list nếu phù hợp với filter hiện tại (trạng thái + loại đơn)
      const orderStatus = orderData.status || data.current_status;
      const rc = Number((orderData as any).requires_counting);
      const kindOk =
        orderKind === 'all' ||
        (orderKind === 'counting' && rc === 1) ||
        (orderKind === 'normal' && rc !== 1) ||
        (orderKind === 'rewash' && Number((orderData as any).order_type) === 2);
      if (matchesStatusFilter(orderStatus) && kindOk) {
        addOrderToList(orderData as OrderListItem);
      }
    }, [matchesStatusFilter, orderKind, addOrderToList]),
    onOrderStatusChanged: useCallback((data: OrderEventData) => {
      console.log('[Socket] ORDER_STATUS_CHANGED event received:', data);
      // Backend gửi order_data, không phải order
      const orderData = data.order_data || data.order;
      if (!orderData) {
        console.log('[Socket] Status changed but no order_data, updating by id:', data);
        // Vẫn có thể cập nhật status nếu có order_id
        if (data.order_id) {
          if (isAllStatuses) {
            updateOrderInList({ id: data.order_id, status: data.current_status } as OrderListItem);
          } else if (matchesStatusFilter(data.current_status)) {
            // Không có full data, reload list
            loadOrders();
          } else {
            removeOrderFromList(data.order_id);
          }
          syncDetailOrderIfOpen(data.order_id);
        }
        return;
      }
      const order = orderData;
      const orderStatus = order.status || data.current_status;
      // Nếu đang ở tab "all" -> cập nhật order
      // Nếu đang ở tab cụ thể -> kiểm tra status
      if (isAllStatuses) {
        updateOrderInList(order as OrderListItem);
      } else if (matchesStatusFilter(orderStatus)) {
        // Order mới đổi sang status này -> thêm vào hoặc cập nhật
        const exists = orders.some(o => o.id === order.id);
        if (exists) {
          updateOrderInList(order as OrderListItem);
        } else {
          addOrderToList(order as OrderListItem);
        }
      } else {
        // Order đổi sang status khác -> xóa khỏi list hiện tại
        removeOrderFromList(order.id);
      }
      syncDetailOrderIfOpen(order.id);
    }, [isAllStatuses, matchesStatusFilter, orders, updateOrderInList, addOrderToList, removeOrderFromList, loadOrders, syncDetailOrderIfOpen]),
    onOrderCancelled: useCallback((data: OrderEventData) => {
      console.log('[Socket] ORDER_CANCELLED event received:', data);
      const orderData = data.order_data || data.order;
      const orderId = orderData?.id || data.order_id;

      // Không hiện toast ở đây - tránh trùng với toast từ API call cancel
      // Chỉ cập nhật danh sách realtime
      if (isAllStatuses) {
        updateOrderInList({ id: orderId, status: ORDER_STATUS.CANCELED } as OrderListItem);
      } else if (matchesStatusFilter(ORDER_STATUS.CANCELED)) {
        if (orderData) {
          addOrderToList(orderData as OrderListItem);
        }
      } else {
        removeOrderFromList(orderId);
      }
      syncDetailOrderIfOpen(orderId);
    }, [isAllStatuses, matchesStatusFilter, addOrderToList, updateOrderInList, removeOrderFromList, syncDetailOrderIfOpen]),
    onOrderAssigned: useCallback((data: OrderEventData) => {
      // Khi nhân viên được gắn vào đơn hàng (pickup, delivery, laundry)
      console.log('[Socket] Order assigned event received:', data);
      const orderData = data.order_data || data.order;
      const orderId = orderData?.id || data.order_id;
      const orderCode = orderData?.code || data.order_code;

      // Nếu có đầy đủ order_data, cập nhật trực tiếp trong list
      if (orderData) {
        updateOrderInList(orderData as OrderListItem);
      } else if (orderId) {
        // Không có full data, reload list để lấy data mới nhất
        console.log('[Socket] Order assigned but no order_data, reloading list for order:', orderId);
        loadOrders();
      }
      syncDetailOrderIfOpen(orderId);
    }, [updateOrderInList, loadOrders, syncDetailOrderIfOpen]),
    onOrderStepAdvanced: useCallback((data: OrderEventData) => {
      console.log('[Socket] ORDER_STEP_ADVANCED event received:', data);
      const orderData = data.order_data || data.order;
      if (orderData) {
        updateOrderInList(orderData as OrderListItem);
      } else if (data.order_id) {
        loadOrders();
      }
      syncDetailOrderIfOpen(orderData?.id || data.order_id);
    }, [updateOrderInList, loadOrders, syncDetailOrderIfOpen]),
    onOrderSplit: useCallback((data: OrderEventData) => {
      console.log('[Socket] ORDER_SPLIT event received:', data);
      // Reload list to get parent + sub-orders
      loadOrders();
      // Nếu đang xem chi tiết đơn cha → cập nhật ngay (has_sub_orders, sub_orders list)
      syncDetailOrderIfOpen(data.order_id);
    }, [loadOrders, syncDetailOrderIfOpen]),
  });

  // =========================
  // Render
  // =========================
  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Danh sách đơn hàng</h1>
          <p className="text-sm text-slate-500 mt-1">Quản lý và theo dõi đơn hàng</p>
        </div>
        {/* Socket Connection Status - hidden */}
        {canExport && (
          // Xuất theo ĐÚNG bộ lọc đang chọn (như loadOrders, bỏ phân trang).
          <OrderExportButton
            filters={{
              key_search: useDebounceSearch || undefined,
              ...(branchId ? { branch_id: String(branchId) } : {}),
              ...(startDate ? { from_date: startDate } : {}),
              ...(endDate ? { to_date: endDate } : {}),
              ...(!isAllStatuses ? { statuses: statusFilter } : {}),
              ...(assignedEmployeeId ? { assigned_employee_id: Number(assignedEmployeeId) } : {}),
              ...(orderKind === 'rewash' ? { order_type: 2 } : {}),
              ...(orderKind === 'counting' ? { requires_counting: 1 } : {}),
              ...(orderKind === 'normal' ? { requires_counting: 0 } : {}),
              ...(consignedView ? { scope: 'consigned' } : {}),
            }}
          />
        )}
      </div>

      {/* Chuyển view: Đơn của chi nhánh ↔ Nhận điều chuyển / Giữ hộ */}
      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={() => { setConsignedView(false); setPage(1); }}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition border ${
            !consignedView ? "bg-green-600 text-white border-green-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
          }`}
        >
          Đơn của chi nhánh
        </button>
        <button
          onClick={() => { setConsignedView(true); setPage(1); }}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition border ${
            consignedView ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
          }`}
        >
          Nhận điều chuyển / Giữ hộ
        </button>
        {consignedView && (
          <span className="text-xs text-slate-500">
            Đơn (kể cả đơn con) đang ở chi nhánh bạn nhưng thuộc chủ đơn khác, hoặc sắp được điều chuyển tới.
          </span>
        )}
      </div>

      {/* Filter Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {/* Hàng 1: Status dropdown + Date range + Search */}
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3 p-4">
          {/* Status dropdown - Ở đầu tiên — chọn được NHIỀU trạng thái (bỏ trống = tất cả) */}
          <div className="min-w-[240px]">
            <MultiSelectDropdown
              placeholder="Tất cả trạng thái"
              className="min-w-[240px]"
              dataOptions={STATUS_FILTER_OPTIONS}
              optionSelected={STATUS_FILTER_OPTIONS.filter((o) => statusFilter.includes(o.value))}
              onChange={(selected) => { setStatusFilter(selected.map((o) => Number(o.value))); setPage(1); }}
              getOptionLabel={(o) => o.label}
              getOptionKey={(o) => o.value}
            />
          </div>
          {/* Date range picker */}
          <div className="flex items-center gap-2">
            <DateInputCustom
              type="date"
              value={toDMY(startDate)}
              maxDate={toDMY(endDate) || undefined}
              onChange={(d) => { setStartDate(toYMD(d ?? "")); setPage(1); }}
              className="w-[150px]"
              placeholder="Từ ngày"
            />
            <span className="text-slate-400">→</span>
            <DateInputCustom
              type="date"
              value={toDMY(endDate)}
              minDate={toDMY(startDate) || undefined}
              onChange={(d) => { setEndDate(toYMD(d ?? "")); setPage(1); }}
              className="w-[150px]"
              placeholder="Đến ngày"
            />
          </div>

          {/* Search input - auto search với debounce */}
          <div className="flex-1 relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Tìm mã đơn, SĐT, tên khách hàng..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Hàng 2: Filters nâng cao */}
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3 p-4 bg-slate-50 border-t border-slate-100">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Lọc theo
          </span>

          <div className="flex flex-wrap items-center gap-3 flex-1">
            {/* Chi nhánh */}
            <div className="min-w-[180px]">
              <DropdownSelectOne
                defaultOption={{ value: 0, label: "Tất cả chi nhánh" }}
                options={branchOptions}
                selectedId={branchId === "" ? 0 : Number(branchId)}
                onChange={(opt) => { setBranchId(opt.value === 0 ? "" : opt.value); setAssignedEmployeeId(""); setPage(1); }}
                getOptionLabel={(o) => o.label}
                getOptionKey={(o) => o.value}
                placeholder="Chi nhánh"
                isSearchLocal
              />
            </div>

            {/* Nhân viên */}
            <div className="min-w-[200px]">
              <SelectDropdownAsyncPaginate
                url={API_USER_ROUTER.API_LIST}
                placeholder="Nhân viên"
                enableSelectAll
                selectAllLabel="Tất cả nhân viên"
                onChange={(selected) => {
                  const item = (selected as any)?.[0];
                  setAssignedEmployeeId(item?.isSelectAll ? "" : (item?.id ?? ""));
                }}
                params={{ status: 1, ...(branchId ? { branch_id: Number(branchId) } : {}) }}
              />
            </div>

            {/* Loại đơn: đơn thường vs đơn kiểm đếm (2 luồng xử lý khác nhau) + đơn giặt lại */}
            <div className="min-w-[160px]">
              <select
                value={orderKind}
                onChange={(e) => { setOrderKind(e.target.value as typeof orderKind); setPage(1); }}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
              >
                <option value="all">Tất cả loại đơn</option>
                <option value="normal">Đơn thường</option>
                <option value="counting">Đơn kiểm đếm</option>
                <option value="rewash">Đơn giặt lại</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto overflow-y-auto max-h-[65vh] w-full scroll-smooth">
        <table className="min-w-[1800px] w-full text-sm text-slate-700 border-collapse">
          <thead className="bg-slate-100 text-slate-600 text-xs uppercase sticky top-0 z-10 text-center whitespace-nowrap">
            <tr>
              <th className="px-4 py-3 text-center border border-slate-200 ">STT</th>
              <th className="px-4 py-3 text-center border border-slate-200">Hẹn giờ giao</th>
              <th className="px-4 py-3 text-center border border-slate-200">Ngày tạo</th>
              <th className="px-4 py-3 text-center border border-slate-200">Mã đơn</th>
              <th className="px-4 py-3 text-center border border-slate-200">Khách hàng</th>
              <th className="px-4 py-3 text-center border border-slate-200">Số Điện thoại</th>
              <th className="px-4 py-3 text-center border border-slate-200">NV Lấy hàng</th>
              <th className="px-4 py-3 text-center border border-slate-200">NV Xử lý</th>
              <th className="px-4 py-3 text-center border border-slate-200">NV Giao hàng</th>
              <th className="px-4 py-3 text-center border border-slate-200">Chi nhánh</th>
              <th className="px-4 py-3 text-center border border-slate-200">Thành tiền</th>
              <th className="px-4 py-3 text-center border border-slate-200">Trạng thái</th>
              <th className="px-4 py-3 text-center min-w-[250px] border border-slate-200">Địa chỉ giao hàng</th>
              <th className="px-4 py-3 text-center border border-slate-200">Ghi chú</th>
              <th className="px-4 py-3 text-center border border-slate-200">Số ký nhận</th>
              <th className="px-4 py-3 text-center border border-slate-200">Số ký giặt</th>
              <th className="px-4 py-3 text-center border border-slate-200">Thiết bị</th>
              <th className="px-4 py-3 text-center border border-slate-200">Người tạo</th>
              <th className="px-4 py-3 text-center border border-slate-200">Công nợ</th>
              <th className="px-4 py-3 text-center border border-slate-200">Thanh toán</th>
              <th className="px-4 py-3 text-center border border-slate-200">Lý do Huỷ(nếu có)</th>
              <th className="px-4 py-3 text-center sticky top-0 right-0 bg-slate-100 z-30 shadow-md border border-slate-200">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={21} className="py-6 text-center text-slate-500">
                  Đang tải dữ liệu...
                </td>
              </tr>
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan={21} className="border border-slate-200">
                  <EmptyState message="Không có đơn hàng" />
                </td>
              </tr>
            ) : (
              orders.map((order, idx) => (<>
                <tr key={order.id} className="border-t hover:bg-slate-50 transition group">
                  <td className="px-4 py-3 border border-slate-200 text-center">{(page - 1) * limit + idx + 1}</td>
                  {/* Hẹn giờ giao */}
                  <td className="px-4 py-3 text-center border border-slate-200">
                    {order.scheduled_delivery_at
                      ? (() => {
                          const d = new Date(order.scheduled_delivery_at);
                          const dd = d.getDate().toString().padStart(2, "0");
                          const MM = (d.getMonth() + 1).toString().padStart(2, "0");
                          const yyyy = d.getFullYear();
                          const hh = d.getHours().toString().padStart(2, "0");
                          const mm = d.getMinutes().toString().padStart(2, "0");
                          return `${dd}/${MM}/${yyyy} ${hh}:${mm}`;
                        })()
                      : "—"}
                  </td>
                  {/* Ngày tạo */}
                  <td className="px-4 py-3 text-center border border-slate-200">
                    {order.created_at
                      ? (() => {
                          const raw = String(order.created_at);
                          const d = new Date(
                            raw.endsWith("Z") || raw.includes("+")
                              ? raw
                              : raw + "Z"
                          );
                          const dd = d.getDate().toString().padStart(2, "0");
                          const MM = (d.getMonth() + 1)
                            .toString()
                            .padStart(2, "0");
                          const yyyy = d.getFullYear();
                          const hh = d.getHours().toString().padStart(2, "0");
                          const mm = d.getMinutes().toString().padStart(2, "0");
                          return `${dd}/${MM}/${yyyy} ${hh}:${mm}`;
                        })()
                      : "—"}
                  </td>
                  <td className="px-4 py-3 font-semibold text-slate-800 border border-slate-200">
                    <div className="flex flex-col items-start gap-1">
                    <span className="whitespace-nowrap">{order.code}</span>
                    {Number(order.order_type) === 2 && (
                      <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-[10px] font-semibold rounded-full whitespace-nowrap">
                        Giặt lại
                      </span>
                    )}
                    {Number((order as any).requires_counting) === 1 && (
                      <span className="px-1.5 py-0.5 bg-violet-100 text-violet-700 text-[10px] font-semibold rounded-full whitespace-nowrap">
                        Kiểm đếm
                      </span>
                    )}
                    {order.has_sub_orders === 1 && order.sub_orders && order.sub_orders.length > 0 && (
                      <button
                        className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-semibold rounded-full hover:bg-amber-200 transition whitespace-nowrap"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedOrders(prev => {
                            const next = new Set(prev);
                            next.has(order.id) ? next.delete(order.id) : next.add(order.id);
                            return next;
                          });
                        }}
                      >
                        {expandedOrders.has(order.id) ? '▼' : '▶'} {order.sub_orders.length} đơn con
                      </button>
                    )}
                    </div>
                  </td>
                  <td className="px-4 py-3 border border-slate-200">{order.customer?.name ?? "—"}</td>
                  <td className="px-4 py-3 border border-slate-200">{order.customer?.phone ?? "—"}</td>
                  {/* NV Lấy hàng */}
                  <td className="px-4 py-3 border border-slate-200">
                    {(() => {
                      const pickupEmployees = order.employees?.filter(e => e.role_type === 1 && e.status !== 3) ?? []; // Loại NV bị reject (status=3)
                      if (pickupEmployees.length > 0) {
                        return pickupEmployees.map((e, i) => (
                          <div key={e.id || i} className="whitespace-nowrap">
                            {e.employee?.name || `NV #${e.employee_id}`}
                            {e.status === 1 && <span className="text-xs text-yellow-500 ml-1">(chờ xác nhận)</span>}
                            {e.assigned_weight ? ` - ${formatKg(e.assigned_weight)} kg` : ''}
                          </div>
                        ));
                      }
                      // Kiểm tra có NV bị reject hay chưa (đang chờ gán mới)
                      const hasRejected = order.employees?.some(e => e.role_type === 1 && e.status === 3);
                      if (hasRejected) return <span className="text-orange-500 text-xs">Chờ gán lại...</span>;
                      return order.driverPickup?.name ?? "—";
                    })()}
                  </td>
                  {/* NV Xử lý */}
                  <td className="px-4 py-3 border border-slate-200">
                    {(() => {
                      const PROCESSING_ROLES = [2, 5, 6, 7, 8];
                      const ROLE_LABELS: Record<number, string> = { 2: 'Giặt', 5: 'Giặt', 6: 'Sấy', 7: 'Ủi', 8: 'Gấp' };
                      const ROLE_COLORS: Record<number, string> = {
                        2: 'text-purple-600', 5: 'text-indigo-600',
                        6: 'text-orange-600', 7: 'text-rose-600', 8: 'text-teal-600',
                      };
                      const processingEmployees = order.employees?.filter(e => PROCESSING_ROLES.includes(e.role_type) && e.status !== 3) ?? []; // Loại NV bị reject
                      if (processingEmployees.length > 0) {
                        return processingEmployees.map((e, i) => (
                          <div key={e.id || i} className="whitespace-nowrap text-sm">
                            <span className={`font-medium ${ROLE_COLORS[e.role_type] || 'text-slate-600'}`}>
                              [{ROLE_LABELS[e.role_type] || '?'}]
                            </span>
                            {' '}{e.employee?.name || `NV #${e.employee_id}`}
                            {e.assigned_weight ? ` - ${formatKg(e.assigned_weight)} kg` : ''}
                          </div>
                        ));
                      }
                      return order.employee?.name ?? "—";
                    })()}
                  </td>
                  {/* NV Giao hàng */}
                  <td className="px-4 py-3 border border-slate-200">
                    {(() => {
                      const deliveryEmployees = order.employees?.filter(e => e.role_type === 3 && e.status !== 3) ?? []; // Loại NV bị reject
                      if (deliveryEmployees.length > 0) {
                        return deliveryEmployees.map((e, i) => (
                          <div key={e.id || i} className="whitespace-nowrap">
                            {e.employee?.name || `NV #${e.employee_id}`}
                            {e.assigned_weight ? ` - ${formatKg(e.assigned_weight)} kg` : ''}
                          </div>
                        ));
                      }
                      return order.driverDelivery?.name ?? "—";
                    })()}
                  </td>
                  <td className="px-4 py-3 border border-slate-200">
                    {(() => {
                      const ownerName = order.branch?.name ?? "—";
                      const heldElsewhere = order.current_branch_id && order.current_branch_id !== order.branch_id;
                      return (
                        <div className="flex flex-col items-start gap-0.5">
                          <span className="whitespace-nowrap">{ownerName}</span>
                          {order.parent_order_id ? (
                            <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-semibold rounded-full whitespace-nowrap">
                              Đơn con{order.sub_order_index ? ` #${order.sub_order_index}` : ""}
                            </span>
                          ) : null}
                          {heldElsewhere ? (
                            <span className="text-[11px] text-orange-600 whitespace-nowrap">
                              → đang ở: {order.current_branch?.name ?? `CN #${order.current_branch_id}`}
                            </span>
                          ) : null}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3 text-right border border-slate-200">{formatCurrency(order.total_amount)}</td>
                  <td className="px-4 py-4 text-center border border-slate-200">
                    {/* Đơn cha đang chờ đơn con xử lý → để trống (trạng thái thật nằm ở từng đơn con). */}
                    {Number(order.has_sub_orders) === 1 && Number(order.status) !== ORDER_STATUS.COMPLETE ? (
                      <span className="text-xs text-slate-400"></span>
                    ) : (
                      <span
                        className={`px-2 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap min-w-[120px] inline-block text-center ${
                          STATUS_BADGE_STYLES[Number(order.status)] || "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {ORDER_STATUS_LABELS[Number(order.status)] || "Không rõ"}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-left min-w-[250px] border border-slate-200">{order.address ?? "—"}</td>
                  <td className="px-4 py-3 text-center border border-slate-200">{order.note ?? "—"}</td>
                  <td className="px-4 py-3 border border-slate-200 text-center">
                    {order.pickup_weight ? `${formatKg(order.pickup_weight)} kg` : "—"}
                  </td>
                  <td className="px-4 py-3 border border-slate-200 text-center">
                    {order.final_weight ? `${formatKg(order.final_weight)} kg` : "—"}
                  </td>
                  {/* Thiết bị */}
                  <td className="px-4 py-3 border border-slate-200 text-center relative">
                    <div className="relative inline-block">
                      <button
                        className="px-2.5 py-1 text-xs font-semibold rounded-full transition bg-cyan-50 text-cyan-600 hover:bg-cyan-100 cursor-pointer"
                        onClick={async (e) => {
                          e.stopPropagation();
                          // Toggle popover
                          if (equipmentPopover?.orderId === order.id) {
                            setEquipmentPopover(null);
                            return;
                          }
                          setEquipmentPopover({ orderId: order.id, items: [], loading: true });
                          try {
                            const res = await fetchEquipmentSummary(order.id);
                            if (res?.status === 200) {
                              const data = Array.isArray(res.data) ? res.data : [];
                              setEquipmentPopover({ orderId: order.id, items: data as OrderEquipmentSummaryItem[], loading: false });
                            } else {
                              setEquipmentPopover(null);
                            }
                          } catch {
                            setEquipmentPopover(null);
                          }
                        }}
                        title="Xem thiết bị"
                      >
                        <svg className="w-4 h-4 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                        </svg>
                      </button>
                      {/* Equipment Popover */}
                      {equipmentPopover?.orderId === order.id && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setEquipmentPopover(null)} />
                          <div className="absolute z-50 top-full left-1/2 -translate-x-1/2 mt-2 bg-white rounded-lg shadow-xl border border-slate-200 p-3 min-w-[220px] text-left">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-semibold text-slate-700">Thiết bị đơn {order.code}</span>
                              <button onClick={() => setEquipmentPopover(null)} className="text-slate-400 hover:text-slate-600 text-xs">&#10005;</button>
                            </div>
                            {equipmentPopover.loading ? (
                              <div className="text-xs text-slate-400 text-center py-2">Đang tải...</div>
                            ) : equipmentPopover.items.length === 0 ? (
                              <div className="text-xs text-slate-400 text-center py-2">Không có thiết bị</div>
                            ) : (
                              <div className="space-y-2">
                                {equipmentPopover.items.map((item) => (
                                  <div key={item.type} className="bg-slate-50 rounded px-2 py-1.5">
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs font-semibold text-slate-700">
                                        {EQUIPMENT_TYPE_NAME[item.type] || `Loại ${item.type}`}
                                      </span>
                                      <span className="text-xs bg-cyan-100 text-cyan-700 px-1.5 py-0.5 rounded-full font-semibold">
                                        {item.count}
                                      </span>
                                    </div>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                      {item.codes.map((code, i) => (
                                        <span key={i} className="text-[10px] bg-white border border-slate-200 px-1.5 py-0.5 rounded text-slate-600">
                                          {code}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 border border-slate-200 whitespace-nowrap">
                    {order.employeeCreate?.name ?? "—"}
                  </td>
                  <td className="px-4 py-4 text-left border border-slate-200">
                    <span
                       className={`px-2 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap min-w-[100px] inline-block text-center ${
                        IS_DEBIT_BADGE_STYLES[Number(order.is_debit)] || "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {IS_DEBIT[Number(order.is_debit)] || "Không rõ"}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-left border border-slate-200">
                    <span
                      className={`px-2 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap min-w-[70px] inline-block text-center ${
                        PAYMENT_METHOD_BADGE_STYLES[Number(order.payment_method)] || "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {PAYMENT_METHOD[Number(order.payment_method)] || "Không rõ"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center border border-slate-200">{order.reason_cancel ?? "—"}</td>

                  {/* Hành động */}
                  <td className="px-4 py-3 text-center sticky right-0 bg-white group-hover:bg-slate-50 transition-colors border border-slate-200">
                    <div className="flex justify-center gap-2">
                      {/* Thanh toán: chỉ khi chưa hoàn thành và chưa huỷ */}
                      {order.status !== ORDER_STATUS.COMPLETE && order.status !== ORDER_STATUS.CANCELED && (
                        <button
                        onClick={() => setPayOrder(order)}
                        title="Thanh toán"
                        className="h-8 w-8 flex items-center justify-center rounded-full border border-green-500 text-green-500 hover:bg-green-50 transition-all"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={2}
                            stroke="currentColor"
                            className="h-5 w-5"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M2.25 8.25h19.5M2.25 6a2.25 2.25 0 012.25-2.25h15A2.25 2.25 0 0121.75 6v12a2.25 2.25 0 01-2.25 2.25h-15A2.25 2.25 0 012.25 18V6zm16.5 8.25h.008v.008H18.75v-.008z"
                            />
                          </svg>
                        </button>
                      )}

                      {/* Chỉnh sửa — ẩn khi đơn đã hoàn thành hoặc đã hủy */}
                      {order.status !== ORDER_STATUS.COMPLETE && order.status !== ORDER_STATUS.CANCELED && (
                        <button
                        onClick={() => setEditOrder(order)}
                        title="Cập nhật đơn"
                        className="h-8 w-8 flex items-center justify-center rounded-full border border-green-500 text-green-500 hover:bg-green-500 hover:text-white transition-all"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={2}
                            stroke="currentColor"
                            className="h-4 w-4"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M16.862 3.487a2.121 2.121 0 0 1 3.001 3.001L7.5 18.85l-4.5.75.75-4.5L16.862 3.487z"
                            />
                          </svg>
                        </button>
                      )}

                     {/* Xem chi tiết */}
                     <button
                      onClick={async () => {
                        const res = await fetchDetail(Number(order.id));
                        if (res?.status === 200) setDetailOrder(res.data);
                      }}
                      title="Xem chi tiết"
                      className="h-8 w-8 flex items-center justify-center rounded-full border border-green-500 text-green-500 hover:bg-green-500 hover:text-white transition-all"
                      >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                        className="h-5 w-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.065 7-9.542 7S3.732 16.057 2.458 12z"
                        />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                      </button>


                      {/* Chuyển trạng thái */}
                      {getNextStatusTransition(order) && (
                        <button
                        onClick={() => setChangeStatusOrder(order)}
                        title={getNextStatusTransition(order)?.label || 'Chuyển trạng thái'}
                        className="h-8 w-8 flex items-center justify-center rounded-full border border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white transition-all"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth={2}
                            stroke="currentColor"
                            className="h-4 w-4"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                            />
                          </svg>
                        </button>
                      )}

                      {/* Huỷ đơn — chỉ cho đơn CHƯA hoàn tất/huỷ */}
                      {order.status !== ORDER_STATUS.COMPLETE && order.status !== ORDER_STATUS.CANCELED && (
                        <button
                        onClick={() => setCancelOrder(order)}
                        title="Huỷ đơn"
                        className="h-8 w-8 flex items-center justify-center rounded-full border border-green-500 text-green-500 hover:bg-green-500 hover:text-white transition-all"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                          stroke="currentColor"
                          className="h-5 w-5"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                      )}
                    </div>
                  </td>
                </tr>
                {/* Sub-order rows (expandable) */}
                {expandedOrders.has(order.id) && order.sub_orders && order.sub_orders.map((sub: any) => (
                  <tr key={`sub-${sub.id}`} className="bg-amber-50 border-t border-dashed border-amber-200">
                    {/* STT */}
                    <td className="px-4 py-3 border border-slate-200 text-center text-sm text-gray-400">↳</td>
                    {/* Hẹn giờ giao / Ngày tạo — giống đơn cha → để trống */}
                    <td className="px-4 py-3 border border-slate-200"></td>
                    <td className="px-4 py-3 border border-slate-200"></td>
                    {/* Mã đơn */}
                    <td className="px-4 py-3 border border-slate-200 text-sm">
                      <span className="font-medium text-amber-800">{sub.code}</span>
                    </td>
                    {/* Khách hàng / SĐT — giống đơn cha → để trống */}
                    <td className="px-4 py-3 border border-slate-200"></td>
                    <td className="px-4 py-3 border border-slate-200"></td>
                    {/* NV Lấy hàng / NV Xử lý — trống (nằm ở đơn cha hoặc bước khác) */}
                    <td className="px-4 py-3 border border-slate-200"></td>
                    <td className="px-4 py-3 border border-slate-200"></td>
                    {/* NV Giao hàng — riêng của đơn con */}
                    <td className="px-4 py-3 border border-slate-200 text-sm">
                      {(() => {
                        const emps = (sub.delivery_employees ?? []).filter((e: any) => e.status !== 3);
                        if (emps.length > 0) {
                          return emps.map((e: any, i: number) => (
                            <div key={e.id || i} className="whitespace-nowrap">
                              {e.employee?.name || `NV #${e.employee_id}`}
                            </div>
                          ));
                        }
                        return sub.driverDelivery?.name ?? '—';
                      })()}
                    </td>
                    {/* Chi nhánh — giống đơn cha → để trống */}
                    <td className="px-4 py-3 border border-slate-200"></td>
                    {/* Thành tiền */}
                    <td className="px-4 py-3 border border-slate-200 text-sm text-right">{formatCurrency(sub.total_amount)}</td>
                    {/* Trạng thái */}
                    <td className="px-4 py-3 border border-slate-200 text-center">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${STATUS_BADGE_STYLES[Number(sub.status)] || 'bg-gray-100 text-gray-500'}`}>
                        {ORDER_STATUS_LABELS[Number(sub.status)] || '?'}
                      </span>
                    </td>
                    {/* Địa chỉ / Ghi chú — giống cha → trống */}
                    <td className="px-4 py-3 border border-slate-200"></td>
                    <td className="px-4 py-3 border border-slate-200"></td>
                    {/* Số ký nhận (kg phân bổ) */}
                    <td className="px-4 py-3 border border-slate-200 text-sm text-center">{Number(sub.pickup_weight) > 0 ? `${formatKg(sub.pickup_weight)} kg` : '—'}</td>
                    {/* Số ký giặt */}
                    <td className="px-4 py-3 border border-slate-200 text-sm text-center">{Number(sub.final_weight) > 0 ? `${formatKg(sub.final_weight)} kg` : '—'}</td>
                    {/* Thiết bị — riêng của đơn con */}
                    <td className="px-4 py-3 border border-slate-200 text-center relative">
                      <div className="relative inline-block">
                        <button
                          className="px-2.5 py-1 text-xs font-semibold rounded-full transition bg-cyan-50 text-cyan-600 hover:bg-cyan-100 cursor-pointer"
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (equipmentPopover?.orderId === sub.id) {
                              setEquipmentPopover(null);
                              return;
                            }
                            setEquipmentPopover({ orderId: sub.id, items: [], loading: true });
                            try {
                              const res = await fetchEquipmentSummary(sub.id);
                              if (res?.status === 200) {
                                const data = Array.isArray(res.data) ? res.data : [];
                                setEquipmentPopover({ orderId: sub.id, items: data as OrderEquipmentSummaryItem[], loading: false });
                              } else {
                                setEquipmentPopover(null);
                              }
                            } catch {
                              setEquipmentPopover(null);
                            }
                          }}
                          title="Xem thiết bị"
                        >
                          <svg className="w-4 h-4 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                          </svg>
                        </button>
                        {equipmentPopover?.orderId === sub.id && (
                          <>
                            <div className="fixed inset-0 z-40" onClick={() => setEquipmentPopover(null)} />
                            <div className="absolute z-50 top-full left-1/2 -translate-x-1/2 mt-2 bg-white rounded-lg shadow-xl border border-slate-200 p-3 min-w-[220px] text-left">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-semibold text-slate-700">Thiết bị đơn {sub.code}</span>
                                <button onClick={() => setEquipmentPopover(null)} className="text-slate-400 hover:text-slate-600 text-xs">&#10005;</button>
                              </div>
                              {equipmentPopover.loading ? (
                                <div className="text-xs text-slate-400 text-center py-2">Đang tải...</div>
                              ) : equipmentPopover.items.length === 0 ? (
                                <div className="text-xs text-slate-400 text-center py-2">Không có thiết bị</div>
                              ) : (
                                <div className="space-y-2">
                                  {equipmentPopover.items.map((item) => (
                                    <div key={item.type} className="bg-slate-50 rounded px-2 py-1.5">
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs font-semibold text-slate-700">
                                          {EQUIPMENT_TYPE_NAME[item.type] || `Loại ${item.type}`}
                                        </span>
                                        <span className="text-xs bg-cyan-100 text-cyan-700 px-1.5 py-0.5 rounded-full font-semibold">
                                          {item.count}
                                        </span>
                                      </div>
                                      <div className="mt-1 flex flex-wrap gap-1">
                                        {item.codes.map((code, i) => (
                                          <span key={i} className="text-[10px] bg-white border border-slate-200 px-1.5 py-0.5 rounded text-slate-600">
                                            {code}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </td>
                    {/* Người tạo — đơn con dùng chung người tạo với đơn cha */}
                    <td className="px-4 py-3 border border-slate-200 whitespace-nowrap">
                      {order.employeeCreate?.name ?? '—'}
                    </td>
                    {/* Công nợ / Thanh toán / Lý do huỷ — trống */}
                    <td className="px-4 py-3 border border-slate-200"></td>
                    <td className="px-4 py-3 border border-slate-200"></td>
                    <td className="px-4 py-3 border border-slate-200"></td>
                    {/* Thao tác */}
                    <td className="px-4 py-3 text-center sticky right-0 bg-amber-50 border border-slate-200">
                      <button
                        onClick={async () => {
                          // Fetch chi tiết THẬT của đơn con (không merge data đơn cha).
                          const res = await fetchDetail(sub.id);
                          if (res?.status === 200) setDetailOrder(res.data);
                        }}
                        title="Xem chi tiết đơn con"
                        className="h-8 w-8 flex items-center justify-center rounded-full border border-green-500 text-green-500 hover:bg-green-500 hover:text-white transition-all mx-auto"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.065 7-9.542 7S3.732 16.057 2.458 12z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </>))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
        <div>
          {totalRecords > 0
            ? `Hiển thị ${(page - 1) * limit + 1} đến ${(page - 1) * limit + orders.length} của ${totalRecords} hàng`
            : "Không có dữ liệu"}
        </div>
        <div className="flex items-center gap-3">
          <select
            className="border rounded px-2 py-1"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>

          {(() => {
            const totalPages = Math.max(1, Math.ceil(totalRecords / limit));
            const pages: (number | string)[] = [];
            const add = (p: number | string) => pages.push(p);
            const start = Math.max(1, page - 1);
            const end = Math.min(totalPages, page + 1);
            add(1);
            if (start > 2) add("...");
            for (let p = start; p <= end; p++) if (p !== 1 && p !== totalPages) add(p);
            if (end < totalPages - 1) add("...");
            if (totalPages > 1) add(totalPages);
            return (
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1 border rounded disabled:opacity-50"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  {"<"}
                </button>
                {pages.map((p, idx) =>
                  typeof p === "number" ? (
                    <button
                      key={idx}
                      onClick={() => setPage(p)}
                      className={`w-8 h-8 rounded-full ${p === page ? "bg-green-600 text-white" : "bg-slate-200 text-slate-700 hover:bg-slate-300"}`}
                    >
                      {p}
                    </button>
                  ) : (
                    <span key={idx} className="px-2">{p}</span>
                  )
                )}
                <button
                  className="px-3 py-1 border rounded disabled:opacity-50"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {">"}
                </button>
              </div>
            );
          })()}
        </div>
      </div>

      {/* ===== Modals ===== */}
      <OrderDetailModal
        open={Boolean(detailOrder)}
        order={detailOrder}
        onClose={() => setDetailOrder(null)}
        onReload={loadOrders}
        onRefreshDetail={async (orderId: number) => {
          const res = await fetchDetail(orderId);
          if (res?.status === 200) setDetailOrder(res.data);
        }}
        onEditWeights={() => {
          if (detailOrder) {
            setWeightOrder(detailOrder);
          }
        }}
        onSplitOrder={() => {
          if (detailOrder) {
            setSplitOrder(detailOrder);
          }
        }}
        onViewSubOrder={async (subOrderId: number) => {
          const res = await fetchDetail(subOrderId);
          if (res?.status === 200) setDetailOrder(res.data);
        }}
        onViewParentOrder={async (parentOrderId: number) => {
          const res = await fetchDetail(parentOrderId);
          if (res?.status === 200) setDetailOrder(res.data);
        }}
        onUpdateNote={async (orderId: number, note: string) => {
          const res = await updateOrder(orderId, { note } as any);
          if (res?.status === 200) {
            // Refresh detail to show updated note
            const refreshed = await fetchDetail(orderId);
            if (refreshed?.status === 200) setDetailOrder(refreshed.data);
            showToast({ message: "Cập nhật ghi chú thành công", type: "success" });
          } else {
            showToast({ message: "Không thể cập nhật ghi chú", type: "error" });
          }
        }}
      />

      <OrderUpdateModal
        open={!!editOrder}
        order={editOrder}
        onClose={() => setEditOrder(null)}
        onSave={async (id, data) => {
          const formatScheduledDeliveryAt = (dateString?: string | null) => {
            if (!dateString) return null;
            const [datePart, timePart] = dateString.split(' ');
            if (!datePart || !timePart) return null;
            const [day, month, year] = datePart.split('/');
            return `${year}-${month}-${day} ${timePart}:00`;
          };
          const payload = {
            ...data,
            scheduled_delivery_at: formatScheduledDeliveryAt(data.scheduled_delivery_at),
          };
          const res = await updateOrder(id, payload as any);
          if (res?.status === 200) {
            showToast({ message: "Cập nhật đơn hàng thành công", type: "success" });
            setEditOrder(null);
            loadOrders();
          } else {
            showToast({ message: res?.message || "Không thể cập nhật đơn hàng", type: "error" });
            return false; // giữ modal mở để người dùng sửa lại
          }
        }}
      />

      <OrderPayModal
        open={!!payOrder}
        order={payOrder}
        onClose={() => setPayOrder(null)}
        onConfirm={async (payload) => {
          if (!payOrder?.id) return;
          try {
            const res = await completeOrderApi(Number(payOrder.id), payload);
            if (res?.status === 200) {
              showToast({ message: "Thanh toán đơn hàng thành công", type: "success" });
              setPayOrder(null);
              loadOrders();
            } else {
              showToast({ message: res?.message || "Không thể hoàn thành đơn hàng", type: "error" });
            }
          } catch (e) {
            showToast({ message: "Lỗi khi hoàn thành đơn hàng", type: "error" });
          }
        }}
      />

      <OrderCancelModal
        open={!!cancelOrder}
        order={cancelOrder}
        onClose={() => setCancelOrder(null)}
        onConfirm={async (reason: string) => {
          if (!cancelOrder?.id) return;
          const res = await cancelOrderApi(Number(cancelOrder.id), { reason_cancel: reason });
          if (res?.status === 200) {
            showToast({ message: "Huỷ đơn hàng thành công", type: "success" });
            setCancelOrder(null);
            loadOrders();
          } else {
            showToast({ message: res?.message || "Không thể huỷ đơn hàng", type: "error" });
          }
        }}
      />

      <OrderEmployeeWeightModal
        open={!!weightOrder}
        order={weightOrder}
        onClose={() => setWeightOrder(null)}
        onSaved={async () => {
          // Refresh order detail if open
          if (detailOrder) {
            const res = await fetchDetail(detailOrder.id);
            if (res?.status === 200) {
              setDetailOrder(res.data);
            }
          }
          loadOrders();
        }}
      />

      <OrderChangeStatusModal
        open={!!changeStatusOrder}
        order={changeStatusOrder}
        loading={changeStatusLoading}
        onClose={() => setChangeStatusOrder(null)}
        onConfirm={async (orderId, nextStatus, classificationData, extra) => {
          setChangeStatusLoading(true);
          try {
            const res = await changeStatusApi(orderId, nextStatus, classificationData, extra);
            if (res?.status === 200) {
              showToast({ message: "Chuyển trạng thái thành công", type: "success" });
              setChangeStatusOrder(null);
              loadOrders();
            } else {
              showToast({ message: (res as any)?.message || "Không thể chuyển trạng thái", type: "error" });
            }
          } catch (e) {
            showToast({ message: "Lỗi khi chuyển trạng thái", type: "error" });
          } finally {
            setChangeStatusLoading(false);
          }
        }}
        onEquipmentDone={(message) => {
          showToast({ message, type: "success" });
          setChangeStatusOrder(null);
          loadOrders();
        }}
        onRefresh={() => loadOrders()}
      />

      <SplitOrderModal
        open={!!splitOrder}
        order={splitOrder}
        loading={splitLoading}
        onClose={() => setSplitOrder(null)}
        onSplit={async (orderId, groups, note) => {
          setSplitLoading(true);
          try {
            const res = await splitOrderApi(orderId, groups, note);
            if (res?.status === 200) {
              showToast({ message: "Chia đơn thành công", type: "success" });
              setSplitOrder(null);
              setDetailOrder(null);
              loadOrders();
            } else {
              showToast({ message: (res as any)?.message || "Không thể chia đơn", type: "error" });
            }
          } catch (e) {
            showToast({ message: "Lỗi khi chia đơn", type: "error" });
          } finally {
            setSplitLoading(false);
          }
        }}
      />
    </div>
  );
}
